import React, { useState } from 'react';
import { Box, Typography, CircularProgress, Paper, Avatar, IconButton, Divider, Collapse, TextField } from '@mui/material';
import { useFeed } from '../../contexts/FeedContext';
import { useAuth } from '../../contexts/AuthContext';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import styled from '@emotion/styled';
import { 
  ChatBubbleOutline, 
  BookmarkBorder, 
  Bookmark, 
  FavoriteBorder, 
  Favorite,
  Send as SendIcon,
  MoreHoriz
} from '@mui/icons-material';
import { Comment } from './Comment';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const PostHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
});

const PostContent = styled(Box)({
  margin: '12px 0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

const PostActions = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: '8px',
  color: 'text.secondary',
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '9999px',
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(94, 53, 177, 0.08)',
  },
}));

const Post = ({ post }) => {
  const { user } = useAuth();
  const { addComment } = useFeed();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentContent, setCommentContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const likeCount = post.post_reactions?.filter(r => r.emoji === '❤️').length || 0;
  const commentCount = post.comments?.length || 0;

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await addComment(post.id, commentContent);
      setCommentContent('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleComments = () => {
    setShowComments(!showComments);
  };

  return (
    <StyledPaper elevation={0}>
      <PostHeader>
        <Avatar 
          src={post.post_metadata?.project_avatar_url || post.post_metadata?.org_avatar_url}
          sx={{ 
            width: 48, 
            height: 48, 
            mr: 2,
            bgcolor: 'primary.main'
          }}
        >
          {(!post.post_metadata?.project_avatar_url && !post.post_metadata?.org_avatar_url) && (post.post_metadata?.icon || '🏢')}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {post.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>·</Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {post.post_metadata?.project_name || 'Organization Activity'}
          </Typography>
        </Box>
        <IconButton size="small">
          <MoreHoriz />
        </IconButton>
      </PostHeader>

      <PostContent>
        <Typography variant="body1" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{post.content}</Typography>
        
        {/* Comment Count */}
        {commentCount > 0 && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{
              display: 'inline-block',
              mt: 0.5,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main'
              }
            }}
            onClick={toggleComments}
          >
            {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
          </Typography>
        )}
      </PostContent>

      <Divider sx={{ my: 2 }} />
      
      {/* Comments Section */}
      <Collapse in={showComments} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {/* Comment Input */}
          {user && (
            <Box component="form" onSubmit={handleAddComment} sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Avatar src={user.user_metadata?.avatar_url} sx={{ width: 32, height: 32 }} />
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Write a comment..."
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton 
                      type="submit" 
                      color="primary" 
                      disabled={!commentContent.trim() || isSubmitting}
                      size="small"
                      aria-label="send-comment"
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          )}
          
          {/* Comments List */}
          {post.comments?.map(comment => (
            <Comment 
              key={comment.id} 
              comment={comment} 
              onDelete={() => {
                // This will be handled by the Comment component
              }} 
            />
          ))}
        </Box>
      </Collapse>

      <PostActions>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ActionButton 
            data-testid={`like-button-${post.id}`}
            onClick={() => setIsLiked(!isLiked)}
            sx={{ color: isLiked ? 'error.main' : 'inherit' }}
          >
            {isLiked ? (
              <Favorite fontSize="small" />
            ) : (
              <FavoriteBorder fontSize="small" />
            )}
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {likeCount > 0 ? likeCount : ''}
            </Typography>
          </ActionButton>
          
          <ActionButton 
            data-testid={`comment-button-${post.id}`}
            onClick={toggleComments}
            sx={{ 
              color: showComments ? 'primary.main' : 'inherit',
              position: 'relative'
            }}
          >
            <ChatBubbleOutline fontSize="small" />
            {commentCount > 0 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  ml: 0.5,
                  color: 'inherit',
                  fontWeight: 500
                }}
              >
                {commentCount}
              </Typography>
            )}
            {showComments && (
              <Box 
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  bgcolor: 'primary.main',
                  borderRadius: '2px'
                }}
              />
            )}
          </ActionButton>
        </Box>
        
        <ActionButton data-testid={`bookmark-button-${post.id}`} onClick={() => setIsBookmarked(!isBookmarked)}>
          {isBookmarked ? <Bookmark color="primary" /> : <BookmarkBorder />}
        </ActionButton>
      </PostActions>
    </StyledPaper>
  );
};
const Feed = () => {
  const { posts, loading, error, ref, hasMore } = useFeed();

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">Error loading feed: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, margin: '0 auto', width: '100%' }}>
      {posts.map((post) => (
        <Post key={post.id} post={post} />
      ))}
      
      {/* Loading indicator or end of feed */}
      <Box ref={ref} py={4} textAlign="center">
        {loading ? (
          <CircularProgress />
        ) : !hasMore ? (
          <Typography color="text.secondary">You've reached the end of the feed</Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default Feed;
